import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { Form, FormInstance, Select } from 'antd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { InputInterface } from '../utils/forms/FormInterface';
import useDragDrop from '../utils/useDragDrop';

import './style.scss';

const { Option } = Select;

interface SelectInputProps {
  formInput: InputInterface;
  index: number;
  form: FormInstance;
  isCreateMode?: boolean;
}

const MultiSelectInput = (props: SelectInputProps) => {
  const { formInput, index, form, isCreateMode } = props;
  const { dragDrop, options, disabledCondition } = formInput.fieldType;
  const fieldValue = form.getFieldValue(formInput.name);

  // Debug logging
  console.log(`MultiSelectInput [${formInput.name}]:`, {
    fieldValue,
    options: formInput.fieldType.options,
    required: formInput.required,
    errorMsg: formInput.errorMsg,
  });

  // Fix: Filter by value instead of name, and handle both string and array field values
  const defaultValue = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];

  console.log(`MultiSelectInput [${formInput.name}] defaultValue:`, defaultValue);

  const labelByValues = options.reduce(
    (acc: { [key: string]: string }, option: { name: string; value: string }) => ({
      ...acc,
      [option.value]: option.name,
    }),
    {}
  );

  const [, setDragDropValues] = useState<any>([]);

  // Handle form value changes properly
  const handleChange = (values: string[]) => {
    console.log(`MultiSelectInput [${formInput.name}] handleChange:`, values);
    // Update form field value
    form.setFieldsValue({ [formInput.name]: values });
    setDragDropValues(values);

    // Trigger form validation for this field
    form.validateFields([formInput.name]).catch(() => {
      // Ignore validation errors here - they'll be shown by the form
    });
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <Form.Item
          key={index}
          label={<div className={!formInput.required ? 'leftOffset' : ''}>{formInput.label}</div>}
          name={formInput.name}
          className="text"
          wrapperCol={{ span: 8, offset: 4 }}
          labelCol={{ span: 4 }}
          labelAlign="left"
          rules={[
            {
              required: formInput.required,
              message: formInput.errorMsg,
            },
          ]}
        >
          <Form.Item
            shouldUpdate={(prevValues, currentValues) => {
              // Re-render when any form value changes if disabledCondition exists
              if (disabledCondition) {
                return JSON.stringify(prevValues) !== JSON.stringify(currentValues);
              }
              return false;
            }}
            noStyle
          >
            {() => {
              // Check if field should be disabled based on readonly or disabledCondition
              const isDisabled =
                (formInput.readonly && !isCreateMode) ||
                (disabledCondition && disabledCondition(form.getFieldsValue()));

              return (
                <Select
                  mode="multiple"
                  placeholder={formInput.placeholder}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  disabled={isDisabled}
                  value={defaultValue}
                  onChange={handleChange}
                  dropdownClassName="multiselect-checkbox-dropdown"
                >
                  {formInput.fieldType.options.map((obj: any, index: number) => (
                    <Option key={index} value={obj.value}>
                      {obj.name}
                    </Option>
                  ))}
                </Select>
              );
            }}
          </Form.Item>
        </Form.Item>
        {dragDrop && (
          <Form.Item
            label={
              <span className="labelWithOffset dragDroplabel">
                Drag options to change the order
              </span>
            }
            labelAlign="left"
            colon={false}
            wrapperCol={{ span: 8, offset: 4 }}
            labelCol={{ span: 4 }}
          >
            <div className="dragDropContainer">
              {fieldValue &&
                fieldValue.map((value: string, index: number) => (
                  <Tag
                    key={value}
                    label={labelByValues[value]}
                    index={index}
                    form={form}
                    formInput={formInput}
                    setDragDropValues={setDragDropValues}
                  />
                ))}
            </div>
          </Form.Item>
        )}
      </DndProvider>
    </>
  );
};

const Tag = (props: any) => {
  const { label, index, form, formInput, setDragDropValues } = props;

  const ref = useDragDrop(form, index, formInput, setDragDropValues);

  return (
    <div ref={ref}>
      <span className="tag">{label}</span>
    </div>
  );
};

export default MultiSelectInput;
